import {useQuery} from '@aboutyou/data-fetcher';
import React, {useEffect} from 'react';
import {normalizePaymentMethods} from './helper';
import {
  CanceledHandlerEvent,
  ErrorHandlerEvent,
  ExpressCheckoutUserData,
  PaymentEventName,
  SDKInstance,
  StartHandlerEvent,
  SuccessHandlerEvent,
} from './types';

interface AuthorizeSdkConfig {
  source: string;
  basketId: string;
  customerId: number | null;
  platformAppId: number;
  baseUrl: string;
  totalPrice: number;
  session: string;
  checkoutRoute: string;
  device: string;
}

const useAuthorizeSdk = (
  sdk: SDKInstance,
  {totalPrice, ...authorizeConfig}: AuthorizeSdkConfig,
) => {
  const authorizeSdk = async (): Promise<ExpressCheckoutUserData | null> => {
    if (totalPrice <= 0) {
      return null;
    }
    sdk.init(authorizeConfig);
    const result = await sdk.authorize({
      platformAppId: authorizeConfig.platformAppId,
      source: authorizeConfig.source,
    });
    return normalizePaymentMethods(result);
  };

  const authorizeSdkDependencies = [
    totalPrice,
    ...Object.values(authorizeConfig),
  ].join(',');

  return useQuery(authorizeSdkDependencies, authorizeSdk, {});
};

export interface ExpressCheckoutWrapperProps {
  /**
   * The source where the express checkout is implemented. This is only used for logging
   */
  source: string;
  /**
   * The checkout basket id
   */
  basketId: string;
  /**
   * Total price of the basket elements. This can also serve as a baskethash.
   */
  totalPrice: number;
  /**
   * customer id is a number (223231) for logged in users
   * must be null for non logged in users
   */
  customerId: number | null;
  /**
   * The id of the shop
   */
  platformAppId: number;
  /**
   * Endpoint the sdk should talk to
   */
  baseUrl: string;
  /**
   * Checkout session cookie value, used to help with syncing problems between tadarida and checkout BE
   * Currently used for paypal express checkout
   */
  session: string;
  /**
   * FE route pointing to the CO, this is used as a fallback from the Express Checkout in case there's an error and
   * we want to redirect the user
   */
  checkoutRoute: string;
  /**
   * Device value, used to help with problem where paypal is redirecting mobile to desktop OSP
   * Currently used for paypal express checkout
   */
  device: string;
  /**
   * An element that should be rendered or a function, receiving the current status of the payment as it's argument
   */
  children?:
    | React.ReactElement
    | ((props: {data: ExpressCheckoutUserData}) => React.ReactNode);
  sdk: SDKInstance;
  campaignKey: string | null;
  /**
   * Fires in case a payment was started. This indicated a loading state of the payment.
   */
  onPaymentStart?: (event: StartHandlerEvent) => void;
  /**
   * Fires in case a payment was successful.
   */
  onPaymentSuccess?: (event: SuccessHandlerEvent) => void;
  /**
   * Fires in case a payment was not successful.
   */
  onPaymentError?: (event: ErrorHandlerEvent) => void;
  /**
   * Fires in case the payment was canceled by the user.
   */
  onPaymentCanceled?: (event: CanceledHandlerEvent) => void;
}

export const ExpressCheckout: React.FC<ExpressCheckoutWrapperProps> = props => {
  const {
    children,
    sdk,
    onPaymentStart,
    onPaymentSuccess,
    onPaymentError,
    onPaymentCanceled,
    ...authorizeProps
  } = props;
  // eslint-disable-next-line local-rules/too-complex-useeffects -- TODO: Cleanup complex useEffect https://aboutyou.atlassian.net/browse/ASFE-7605e-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    const methods = {
      [PaymentEventName.PAYMENT_START]: onPaymentStart,
      [PaymentEventName.PAYMENT_SUCCESS]: onPaymentSuccess,
      [PaymentEventName.PAYMENT_ERROR]: onPaymentError,
      [PaymentEventName.PAYMENT_CANCELED]: onPaymentCanceled,
    };
    Object.entries(methods).forEach(([key, method]) => {
      if (method) {
        sdk.subscribe(key as any, method as any);
      }
    });
    return () => {
      Object.entries(methods).forEach(([key, method]) => {
        if (method) {
          sdk.unsubscribe(key as any, method as any);
        }
      });
    };
  }, [
    sdk,
    onPaymentStart,
    onPaymentSuccess,
    onPaymentError,
    onPaymentCanceled,
  ]);

  const {
    data: renderPropsData,
    isLoading,
    isError,
  } = useAuthorizeSdk(sdk, authorizeProps);

  if (isLoading || isError || !renderPropsData) {
    return null;
  }

  const injectedChildren =
    typeof children === 'function'
      ? children({data: renderPropsData})
      : children;

  return <>{injectedChildren}</>;
};
